/**
 * CompetenceChip — one competence in a Competence Map band grid.
 *
 * Spanish competence name + state color (server-computed `state`, never
 * re-derived) and a subtle "expected hard" badge where there is no evidence
 * yet and contrastive difficulty >= 4.
 *
 * Rendered up to 62 times per screen — React.memo'd, callbacks stable.
 *
 * @see docs/specs/mobile/competence-map.mobile.md
 */

import React, { useCallback } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FS_XS, FS_SM } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import type { CompetenceMapItem } from '../../generated/api/http-contracts';
import { translateErrorType } from '../practice/recommendedText';
import { chipA11yLabel, showsExpectedHardBadge, type CompetenceState } from './competenceMapText';

interface CompetenceChipProps {
  competence: CompetenceMapItem;
  onPress: (competence: CompetenceMapItem) => void;
  /**
   * testID namespace. A due competence renders twice — once in the due
   * section, once inside its own band — so the two copies must not collide on
   * one id: a duplicate testID makes `getByTestId` throw and would have made
   * every band assertion in the tests unresolvable.
   */
  testIDPrefix?: string;
}

/**
 * State → semantic theme colors (soft background + border accent).
 *
 * `upcoming` is muted and visibly NOT a failure: it must never borrow the
 * error tokens `struggling` owns. It is also deliberately quieter than
 * `untried` — "we have not seen this" is an invitation, "this comes later" is
 * only context.
 *
 * "Quieter" is a fill difference, NOT a missing outline. The first version
 * set both `bg` and `border` to `inputBg`, which produced a chip with no
 * boundary at all (1.05:1 against the page in light mode) — an invisible
 * affordance on two thirds of a beginner's map — and, because `inputBg` is
 * *lighter* than `card` in dark mode, made `upcoming` LOUDER than `untried`
 * there, inverting the hierarchy this comment claims. Sitting `upcoming` on
 * the page background keeps it recessive against `untried`'s raised card in
 * both themes, while `theme.border` keeps it legibly a button.
 */
function useStateColors(state: CompetenceState) {
  const { theme } = useDisplayTheme();
  switch (state) {
    case 'mastered': return { bg: theme.successBg, border: theme.success, label: theme.text };
    case 'learning': return { bg: theme.warningBg, border: theme.warning, label: theme.text };
    case 'struggling': return { bg: theme.errorBg, border: theme.error, label: theme.text };
    case 'untried': return { bg: theme.card, border: theme.border, label: theme.text };
    case 'upcoming': return { bg: theme.background, border: theme.border, label: theme.textSecondary };
    // A state this client does not know about is rendered, not crashed. The
    // switch is exhaustive over today's union, so this is unreachable at
    // compile time — but the response is not validated at runtime, and the
    // N-1 contract promises graceful degradation, not a redbox, the day the
    // server grows a sixth state.
    default: return { bg: theme.card, border: theme.border, label: theme.text };
  }
}

export const CompetenceChip = React.memo(function CompetenceChip({
  competence,
  onPress,
  testIDPrefix = 'competence-map-chip',
}: CompetenceChipProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const stateColors = useStateColors(competence.state);

  const handlePress = useCallback(() => {
    onPress(competence);
  }, [onPress, competence]);

  const name = translateErrorType(t, competence.slug);
  const showHardBadge = showsExpectedHardBadge(competence);

  return (
    <TouchableOpacity
      testID={`${testIDPrefix}-${competence.slug}`}
      accessibilityRole="button"
      accessibilityLabel={chipA11yLabel(t, name, competence)}
      onPress={handlePress}
      activeOpacity={0.7}
      style={[styles.chip, { backgroundColor: stateColors.bg, borderColor: stateColors.border }]}
    >
      <Text
        allowFontScaling={false}
        numberOfLines={2}
        style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: stateColors.label, ...ffs }}
      >
        {name}
      </Text>
      {competence.isDue && (
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
        >
          {t('competenceMap.chip.due')}
        </Text>
      )}
      {showHardBadge && (
        <View
          testID={`${testIDPrefix}-hard-${competence.slug}`}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
          style={[styles.hardBadge, { backgroundColor: theme.warning, borderColor: theme.card }]}
        >
          <Text allowFontScaling={false} style={styles.hardBadgeText}>
            !
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  chip: {
    maxWidth: '100%',
    minHeight: 48,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: 'center',
  },
  hardBadge: {
    position: 'absolute',
    top: -5,
    right: -5,
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hardBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: 12,
  },
});
